/**
 * The snapshot rate a client asks for (D1 `q-snapshot-rate-switch`; snapshot-delivery.md §5).
 *
 * `?snaphz=10` (or `15`) in the page URL makes this client's hello and resume carry
 * `snapshotHz`, so ONE session can be compared at 10 Hz and at 15 Hz on the same server without
 * a redeploy — the owner's playtest (ruling 2026-10-21T12:23Z: "test at both 10 and 15 to get
 * feel and performance measured"). The server honours a value in SNAPSHOT_HZ_CHOICES and tells
 * the client the rate it will use in `helloAck.snapshotHz`; the client sizes its interpolation
 * delay from that echo, never from this request.
 *
 * Absent or any other value ⇒ undefined: the hello carries no `snapshotHz` and is byte-identical
 * to the build before D1, so an ordinary player never sends it. Production honours the URL
 * parameter too (unlike the dev-only panel overrides): it is a per-player choice, and every rate
 * it can select is one the server is built to serve.
 *
 * Pure (no DOM): the caller passes the search string.
 */
import { isSnapshotHz } from '@great-cloud/shared/client';

/** The URL query parameter that turns binary snapshots off for this tab (D4): `?snapbin=0`. */
export const SNAPSHOT_BIN_PARAM = 'snapbin';

/**
 * Does this client advertise `snapshotBin1` (D4 `q-snapshot-binary-wire`; snapshot-delivery.md
 * §4.4)? Yes unless the page URL says `?snapbin=0` (or `off`/`false`) — the comparison and
 * escape hatch for a playtest. The server decides whether it grants it (`GC_SNAPSHOT_BIN`).
 */
export function binarySnapshotsWanted(search: string): boolean {
  let raw: string | null;
  try {
    raw = new URLSearchParams(search).get(SNAPSHOT_BIN_PARAM);
  } catch {
    return true;
  }
  if (raw === null) return true;
  const v = raw.trim().toLowerCase();
  return !(v === '0' || v === 'off' || v === 'false');
}

/** The URL query parameter that asks for a snapshot rate. */
export const SNAPSHOT_HZ_PARAM = 'snaphz';

export function requestedSnapshotHz(search: string): number | undefined {
  let raw: string | null;
  try {
    raw = new URLSearchParams(search).get(SNAPSHOT_HZ_PARAM);
  } catch {
    return undefined;
  }
  if (raw === null || raw.trim() === '') return undefined;
  const hz = Number(raw.trim());
  return isSnapshotHz(hz) ? hz : undefined;
}
